import { useNavigate } from "react-router-dom";
import { BookOpen, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { ResearchDocument } from "@/hooks/useResearchLibrary";

const CATEGORY_LABELS: Record<string, string> = {
  domain_report: "Domain Report",
  deep_research: "Deep Research",
  strategic_framework: "Strategic Framework",
};

const CATEGORY_COLORS: Record<string, string> = {
  domain_report: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  deep_research: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200",
  strategic_framework: "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200",
};

interface ResearchDocumentTableProps {
  documents: ResearchDocument[];
  isLoading: boolean;
}

export function ResearchDocumentTable({ documents, isLoading }: ResearchDocumentTableProps) {
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (documents.length === 0) {
    return (
      <div className="text-center py-16 text-muted-foreground">
        <BookOpen className="h-8 w-8 mx-auto mb-2 opacity-30" />
        <p className="text-sm">No documents found</p>
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Title</TableHead>
          <TableHead className="w-[160px]">Category</TableHead>
          <TableHead className="w-[180px]">Author</TableHead>
          <TableHead className="w-[130px]">Published</TableHead>
          <TableHead className="w-[80px] text-right">Size</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {documents.map((doc) => {
          const formattedDate = doc.published_at
            ? new Date(doc.published_at).toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
              })
            : "—";

          const fileSizeLabel = doc.file_size_bytes
            ? `${Math.round(doc.file_size_bytes / 1024)} KB`
            : "—";

          return (
            <TableRow
              key={doc.id}
              className="cursor-pointer"
              onClick={() => navigate(`/research-library/${doc.slug}`)}
            >
              <TableCell className="font-medium">{doc.title}</TableCell>
              <TableCell>
                <Badge
                  variant="secondary"
                  className={`text-xs ${CATEGORY_COLORS[doc.category] || ""}`}
                >
                  {CATEGORY_LABELS[doc.category] || doc.category}
                </Badge>
              </TableCell>
              <TableCell className="text-muted-foreground text-sm">{doc.author}</TableCell>
              <TableCell className="text-muted-foreground text-sm">{formattedDate}</TableCell>
              <TableCell className="text-right text-muted-foreground text-sm">{fileSizeLabel}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
